import * as React from 'react';
import {
  useMemo,
  useCallback,
  useState,
  useRef,
  useEffect,
  type FocusEvent,
} from 'react';
import {
  dynamic,
  assignProps,
  createView,
  view,
  createViewConfig,
  toIntent,
  type Intent,
  type Merge,
} from '@sail/engine';
import type {FocusManager} from '@sail/react-aria';
import {
  FocusScope,
  useControlledState,
  useFocusManager,
  useSpinButton,
} from '@sail/react-aria';
import {tokens as $} from '@sail/tokens';
import {
  message,
  useCalendarData,
  useIntl,
  useIntlConfig,
  browserTimeZone,
} from '@sail/intl';
import {css} from '../css';
import {
  weekFieldValueToDate,
  weekFieldValueToEndDate,
} from './date-picker/utils/date-field';
import type {View} from '../view';
import {useFormFieldProps} from './FormField';
import type {FormFieldProps} from './FormField';
import {NativeTextFieldConfig, TextField} from './TextField';
import type {CompositeFieldProps} from './CompositeField';
import {CompositeField} from './CompositeField';
import {composite, rovingTabIndex} from './rovingTabIndex';
import {Tooltip} from './Tooltip';
import {Layer} from './layers/Layer';
import {useFloating} from './popover/useFloating';

export type DateStyle =
  | 'YMD'
  | 'MDY'
  | 'DMY'
  | 'YM'
  | 'MY'
  | 'MD'
  | 'DM'
  | 'WY'
  | 'YW'
  | 'Y';
type DateStyleKey = 'Y' | 'M' | 'D' | 'W';

// This is rough, and we don't want it to change after page load
const now = new Date();

// TODO: Fix this for various different calendar formats.
// e.g. Ethiopic Calendar has 13 months, Hebrew Calendar has 12 or 13 months depending on the year.
// Should probably use a intl library for this.
const minMaxStartPerDateStyle = (
  t: DateStyleKey,
): {min: number; max: number; start: number} => {
  if (t === 'Y') {
    const currentYear = now.getFullYear();
    return {
      min: 1,
      max: currentYear + 100,
      start: currentYear - 1,
    };
  } else if (t === 'M') {
    const currentMonth = now.getMonth();
    return {
      min: 1,
      max: 12,
      start: currentMonth,
    };
  } else if (t === 'D') {
    const currentDay = now.getDate();
    return {
      min: 1,
      max: 31,
      start: currentDay - 1,
    };
  } else if (t === 'W') {
    return {
      min: 1,
      max: 53,
      start: 1,
    };
  }
  return {
    min: 0,
    max: 100,
    start: 0,
  };
};

type DateString = string | undefined;

export type DateFieldProps = Merge<
  FormFieldProps<Omit<CompositeFieldProps, 'onChange'>>,
  {
    /**
     * Programmatically mark the value as invalid
     * @external
     */
    invalid?: boolean;
    /**
     * A default value that a user can change
     * @external
     */
    defaultValue: string;
    /**
     * Handler that is called when a valid date is entered into the field
     * @external
     */
    onDateChange?: (value: string) => void;
    /**
     * Handler that is called for each modification to the field, irrespective of validity
     */
    onChange?: (value: string) => void;
    /**
     * Accepts an object with keys for each field, where values are callbacks that return a string value.
     * Fires on each change to the field, irrespective of validity.
     * @external
     */
    unstable_onFieldChange?: Partial<
      Record<DateStyleKey, (value: string) => void>
    >;
    /**
     * Sets the value directly, cannot be changed by a user
     */
    value?: string;
    /**
     * Descriptive text that will be rendered adjacent to the control's label
     * @external
     */
    description?: React.ReactNode;
    /**
     * @external
     */
    disabled?: boolean;
    /**
     * The size of the input
     * @external
     */
    size: 'small' | 'medium' | 'large';
    /**
     * Order that the year, month, and day fields are displayed
     *
     * If you want to use the format based on the locale Sail Intl is initialized with, you can use the `useLocalizedDateFieldFormat` hook. See the [Localized format](/ui/components/date-field#localized-format) section for more details.
     */
    format:
      | 'YMD'
      | 'MDY'
      | 'DMY'
      | 'YM'
      | 'MY'
      | 'MD'
      | 'DM'
      | 'WY'
      | 'YW'
      | 'Y';
    /**
     * Indicated whether to add leading 0's to each field
     * @external
     */
    shouldPadZeros?: Partial<Record<DateStyleKey, boolean>>;
    /**
     * Controls when to show the inline typing-helper tooltip that displays the date format pattern
     * and highlights the active segment.
     * - "auto" (default): Shows automatically for formats with day and month (DMY, MDY, MD, DM) after both are filled
     * - "always": Always shows when focused, regardless of format or filled values
     * - "never": Never shows the tooltip
     * @external
     */
    inlineHelper?: 'auto' | 'always' | 'never';
    /**
     * The element to use as a separator between date fields
     */
    separator?: React.ReactNode;
    /**
     * Placeholder text for each date segment field
     */
    placeholders?: Partial<Record<DateStyleKey, string>>;
    /**
     * HTML autocomplete attribute values for each date segment field.
     * Accepts an object keyed by segment type ('Y', 'M', 'D', 'W') with
     * autocomplete hint strings as values. See [MDN documentation](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete) for supported values.
     */
    autoComplete?: Partial<Record<DateStyleKey, string>>;
    /**
     * The timezone to use for week date calculations.
     * Defaults to the browser's timezone.
     */
    timeZone?: string;
  }
>;

const Separator = createView(({separator}: {separator: React.ReactNode}) => {
  if (typeof separator === 'string') {
    return (
      <view.span
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        uses={[assignProps({'aria-hidden': true} as any)]}
        css={{bleedX: 'space.75'}}
      >
        {separator || '/'}
      </view.span>
    );
  }
  return separator ? <>{separator}</> : null;
});

const WeekPrefix = createView(({prefix}: {prefix: string | undefined}) => {
  if (!prefix) {
    return null;
  }

  return (
    <view.span
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      uses={[assignProps({'aria-hidden': true} as any)]}
    >
      {prefix}
    </view.span>
  );
});

function cleanNonDigits(v: string): string {
  return v.replace(/\D/g, '');
}

function trimLeadingZeros(v: string): string {
  return v.replace(/^0+/, '');
}

function padLeadingZeros(len: number, v: string): string {
  return v.padStart(len, '0');
}

function formatSingleValue(
  type: DateStyleKey,
  value: string,
  hasManualZero: boolean,
  shouldPadZeros = true,
): string {
  const datePartLength = type === 'Y' ? 4 : 2;
  const noDigits = cleanNonDigits(value);
  const noLeadingZeros = hasManualZero ? noDigits : trimLeadingZeros(noDigits);
  const trimmedToLength = noLeadingZeros.substring(0, datePartLength);
  let formattedValue =
    hasManualZero || !shouldPadZeros
      ? trimmedToLength
      : padLeadingZeros(datePartLength, trimmedToLength);

  // If the value is all zeros, then clear it out
  if (formattedValue.length === datePartLength && /^0+$/.test(formattedValue)) {
    formattedValue = '';
  }

  // Clamp the value to the valid range for the date part type.
  // Only clamp non-year segments (M, D, W) since their max values are fixed
  if (type !== 'Y') {
    const {max} = minMaxStartPerDateStyle(type);
    const numericValue = parseInt(formattedValue, 10);
    if (!isNaN(numericValue) && numericValue > max) {
      formattedValue = shouldPadZeros
        ? padLeadingZeros(datePartLength, `${max}`)
        : `${max}`;
    }
  }

  return formattedValue;
}

// This validates that the actual date has or will occur
const isActualDate = (
  d: Date,
  valueMap: {[_k in DateStyleKey]?: string},
): boolean => {
  const {Y = '', M = '', D = ''} = valueMap;
  const UTCVal = {
    Y: padLeadingZeros(4, String(d.getUTCFullYear())),
    M: padLeadingZeros(2, String(d.getUTCMonth() + 1)),
    D: padLeadingZeros(2, String(d.getUTCDate())),
  };
  if (Y !== UTCVal.Y || M !== UTCVal.M || D !== UTCVal.D) {
    return false;
  }
  return true;
};

// The checks if the date object was constructed at all
// However, just because the date object was constructed it
// doesn't mean the date is an *actual* date. e.g. 2013-02-30
function isValidDateObject(d: Date): boolean {
  return d instanceof Date && !isNaN(d as unknown as number);
}

function formatFullValue({Y = '', M = '', D = ''}): string {
  return [Y, M, D].join('-');
}

function formatChangedValue(value: DateValue, format: DateStyle): string {
  return (['Y', 'M', 'D', 'W'] as DateStyleKey[])
    .filter((type: DateStyleKey) => format.includes(type))
    .map((type: DateStyleKey) => value[type])
    .join('-');
}

function isDatePartLikelyComplete(
  t: DateStyleKey,
  value: string,
  hasManualZero: boolean,
): boolean {
  const datePartLength = t === 'Y' ? 4 : 2;

  // If it's the correct amount of characters AND doesn't start with zero
  if (
    value.length === datePartLength &&
    (value.charAt(0) !== '0' || hasManualZero)
  ) {
    return true;
  }

  // For months, auto-advance if the user types a digit > 1 (e.g., "2" becomes "02")
  if (t === 'M') {
    const numericValue = parseInt(value, 10);
    if (!isNaN(numericValue) && numericValue > 1) {
      return true;
    }
  }

  // For days, auto-advance if the user types a digit > 3 (e.g., "4" becomes "04")
  if (t === 'D') {
    const numericValue = parseInt(value, 10);
    if (!isNaN(numericValue) && numericValue > 3) {
      return true;
    }
  }

  // For weeks, auto-advance if the user types a digit > 5 (e.g., "6" becomes "06")
  if (t === 'W') {
    const numericValue = parseInt(value, 10);
    if (!isNaN(numericValue) && numericValue > 5) {
      return true;
    }
  }

  return false;
}

export const DateFieldConfig = createViewConfig({
  props: {} as DateFieldProps,
  name: 'DateField',
  flattens: true,
  defaults: {
    size: 'medium',
    separator: '/',
    defaultValue: '',
    format: 'YMD',
  },
});

function isValidDateString(dateString: string, format: string): boolean {
  const splitValues = dateString.split('-');
  return dateString.length > 0 && splitValues.length === format.length;
}

function parseDate(dateString: string, format: DateStyle): DateValue {
  if (!isValidDateString(dateString, format)) {
    return {
      D: '',
      M: '',
      Y: '',
      W: '',
    };
  }

  // Possible dateString value formats
  // Y-M-D
  // Y-M
  // M-D
  // Y-W
  const splitValues = dateString.split('-');

  switch (format) {
    case 'Y':
      return {
        D: '',
        M: '',
        Y: splitValues[0] || '',
        W: '',
      };
    case 'YM':
    case 'MY':
      return {
        D: '01',
        M: splitValues[1] || '',
        Y: splitValues[0] || '',
        W: '',
      };
    case 'MD':
    case 'DM':
      return {
        D: splitValues[1] || '',
        M: splitValues[0] || '',
        Y: '2024', // 2024 is a leap year and it will work for any date
        W: '',
      };
    case 'WY':
    case 'YW':
      return {
        D: '',
        M: '',
        Y: splitValues[0] || '',
        W: splitValues[1] || '',
      };
    default:
      return {
        D: splitValues[2] || '',
        M: splitValues[1] || '',
        Y: splitValues[0] || '',
        W: '',
      };
  }
}

type SegmentProps = React.ComponentProps<typeof TextField>;

const yearLabel = message({
  id: '@sail/ui.datefield.yearLabel',
  description: 'The accessible label for the year input in a date field',
  defaultMessage: 'Year',
});

const monthLabel = message({
  id: '@sail/ui.datefield.monthLabel',
  description: 'The accessible label for the month input in a date field',
  defaultMessage: 'Month',
});

const dayLabel = message({
  id: '@sail/ui.datefield.dayLabel',
  description: 'The accessible label for the day input in a date field',
  defaultMessage: 'Day',
});

const weekLabel = message({
  id: '@sail/ui.datefield.weekLabel',
  description: 'The accessible label for the week input in a date field',
  defaultMessage: 'Week',
});

const inputMap = {
  Y: {
    placeholder: 'YYYY',
    pattern: '[0-9]{4}',
  },
  M: {
    placeholder: 'MM',
    pattern: '[0-1][0-9]',
  },
  D: {
    placeholder: 'DD',
    pattern: '[0-3][0-9]',
  },
  W: {
    placeholder: 'WW',
    pattern: '[0-5][0-9]',
  },
};

type DateValue = {D: string; M: string; Y: string; W: string};
type ZeroPad = {D: boolean; M: boolean; Y: boolean; W: boolean};

type TooltipState = {
  focusedSegment: DateStyleKey | null;
  dismissed: boolean;
};

type DateFieldState = {
  value: DateString;
  dateParts: DateValue;
  setSegment(key: DateStyleKey, value: string): void;
  increment(key: DateStyleKey): void;
  decrement(key: DateStyleKey): void;
  manualZero: ZeroPad;
  setManualZero(manualZero: ZeroPad): void;
  props: Pick<
    DateFieldProps,
    'shouldPadZeros' | 'placeholders' | 'autoComplete'
  >;
  tooltipState: TooltipState;
  setTooltipState(
    state: TooltipState | ((prev: TooltipState) => TooltipState),
  ): void;
};

const segmentLabelMap = {
  Y: yearLabel,
  M: monthLabel,
  D: dayLabel,
  W: weekLabel,
};

function useDateSegment(
  type: DateStyleKey,
  state: DateFieldState,
): View.Intent<Partial<SegmentProps>> {
  const intl = useIntl();
  const value = state.dateParts[type];
  const minMaxStart = minMaxStartPerDateStyle(type);
  const {shouldPadZeros} = state.props;
  const focusManager = useRef<FocusManager>(null);
  const {setTooltipState, manualZero, setManualZero, setSegment} = state;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;

      let hasManualZero = manualZero[type];
      if (val === '0') {
        hasManualZero = true;
        setManualZero({...manualZero, [type]: true});
      } else if (val === '' || val.length === 1) {
        hasManualZero = false;
        setManualZero({...manualZero, [type]: false});
      }

      const formattedValue = formatSingleValue(
        type,
        val,
        hasManualZero,
        shouldPadZeros?.[type],
      );

      setSegment(type, formattedValue);

      if (isDatePartLikelyComplete(type, formattedValue, hasManualZero)) {
        focusManager.current?.focusNext({
          from: e.target as HTMLElement,
          tabbable: true,
        });
      }
    },
    [type, shouldPadZeros, focusManager, manualZero, setSegment, setManualZero],
  );

  const monthName = useMemo(() => {
    if (type !== 'M' || !value) {
      return undefined;
    }

    const monthIndex = parseInt(value, 10) - 1;

    if (monthIndex >= 0 && monthIndex < 12) {
      return intl.formatDateTime(new Date(2026, monthIndex, 1), {
        style: 'long-month',
      });
    }

    return undefined;
  }, [value, type, intl]);

  const {spinButtonProps} = useSpinButton({
    value: value ? parseInt(value, 10) : undefined,
    textValue: monthName
      ? `${parseInt(value, 10)} – ${monthName}`
      : value || '',
    minValue: minMaxStart.min,
    maxValue: minMaxStart.max,
    onIncrement: () => state.increment(type),
    onDecrement: () => state.decrement(type),
    onIncrementToMax: () => state.setSegment(type, `${minMaxStart.max}`),
    onDecrementToMin: () => state.setSegment(type, `${minMaxStart.min}`),
  });

  // Remove ARIA value attributes from props applied to the <input>.
  // VoiceOver doesn't respect aria-valuetext on native <input> elements and instead
  // computes a percentage from aria-valuenow/min/max or defaults to reading "0".
  const {
    'aria-valuenow': _valuenow,
    'aria-valuetext': _valuetext,
    'aria-valuemin': _valuemin,
    'aria-valuemax': _valuemax,
    ...spinButtonInputProps
  } = spinButtonProps;

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const el = e.target as HTMLInputElement;

      if (e.key === 'Backspace' && el.value === '') {
        focusManager.current?.focusPrevious({
          from: el,
          tabbable: true,
        });
      }
    },
    [focusManager],
  );

  const onFocus = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const el = e.target;

      if (el.value?.length > 0) {
        el.setSelectionRange(0, 4);
      }

      setTooltipState((prev) => ({...prev, focusedSegment: type}));
    },
    [type, setTooltipState],
  );

  const onBlur = useCallback(() => {
    setTooltipState((prev) => ({...prev, focusedSegment: null}));
  }, [setTooltipState]);

  return toIntent([
    dynamic(function DateSegment() {
      const current = useFocusManager();
      // use the FocusScope's focus manager to enable programmatic control over moving focus
      Object.assign(focusManager, {current});
    }),
    assignProps<SegmentProps>({
      value,
      inputMode: 'numeric',
      hiddenElements: ['label'],
      onChange: handleChange,
      label: intl.formatMessage(segmentLabelMap[type]),
      ...inputMap[type],
      placeholder:
        state.props.placeholders?.[type] || inputMap[type].placeholder,
      autoComplete: state.props.autoComplete?.[type],
      ...spinButtonInputProps,
    }),
    assignProps<SegmentProps>({
      // select the whole input value when the input is clicked
      onClick: (event: React.MouseEvent<HTMLInputElement>) => {
        const target = event.target as HTMLInputElement;

        target.select();
      },
      // prevent the browser from changing the caret position
      onMouseUp: (event: React.MouseEvent<HTMLInputElement>) => {
        event.preventDefault();
      },
      // select the whole input value when the input is focused
      onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
        event.target.select();
      },
    }),
    rovingTabIndex as Intent,
    // roving tab index will remove the element from the tab sequence
    // but date field is a bit of a special case where we still enable users to tab to each segment.
    assignProps({tabIndex: 0}),
    assignProps({onFocus, onBlur, onKeyDown}) as Intent,
    value.length === 0 ? css({caretColor: 'transparent'}) : css({}),
  ]);
}

function useDateFieldState(
  props: Partial<DateFieldProps> & {format: DateStyle},
): DateFieldState {
  const [value, setValue] = useControlledState<DateString>(
    props.value,
    props.defaultValue,
    // NOTE: useControlledState optional arg, but it's not reflected in the type definition
    props.onChange as (value: DateString) => void,
  );

  const dateParts = useMemo(() => {
    const parsed = parseDate(value || '', props.format!);
    const normalized = {...parsed};
    (['Y', 'M', 'D', 'W'] as DateStyleKey[]).forEach((key) => {
      const part = parsed[key];
      if (part && props.shouldPadZeros?.[key] !== false) {
        const num = parseInt(part, 10);
        if (!isNaN(num) && num > 0) {
          normalized[key] = part.padStart(key === 'Y' ? 4 : 2, '0');
        }
      }
    });
    return normalized;
  }, [value, props.format, props.shouldPadZeros]);

  const [manualZero, setManualZero] = useState({
    D: dateParts.D.startsWith('0'),
    M: dateParts.M.startsWith('0'),
    Y: dateParts.Y.startsWith('0'),
    W: dateParts.W.startsWith('0'),
  });

  const [tooltipState, setTooltipState] = useState<TooltipState>({
    focusedSegment: null,
    dismissed: false,
  });

  const {onDateChange, unstable_onFieldChange} = props;

  const setSegment = useCallback(
    (key: DateStyleKey, value: string) => {
      const newValue = {...dateParts, [key]: value};
      const formatted = formatChangedValue(newValue, props.format!);
      // update the value of the control to reflect the formatted value
      // triggering change event as needed
      setValue(formatted);

      if (unstable_onFieldChange) {
        unstable_onFieldChange[key]?.(value);
      }

      const d = new Date(formatFullValue(newValue));

      if (isValidDateObject(d) && isActualDate(d, newValue)) {
        onDateChange?.(formatted);
      }
    },
    [dateParts, onDateChange, unstable_onFieldChange, setValue, props.format],
  );

  /**
   * Modifies the a specific segment of the date by the given amount,
   * cycling the value to the min/max if needed.
   */
  const adjustSegment = useCallback(
    (key: DateStyleKey, amount: number) => {
      const minMaxStart = minMaxStartPerDateStyle(key);
      const currentString = dateParts[key];
      const currentValue = currentString
        ? parseInt(cleanNonDigits(currentString), 10)
        : minMaxStart.start;

      let nextValue = currentValue + amount;

      if (nextValue < minMaxStart.min) {
        nextValue = minMaxStart.max;
      }
      if (nextValue > minMaxStart.max) {
        nextValue = minMaxStart.min;
      }

      const formatted = formatSingleValue(key, `${nextValue}`, false, true);

      setSegment(key, formatted);
    },
    [dateParts, setSegment],
  );

  const increment = useCallback(
    (key: DateStyleKey) => adjustSegment(key, 1),
    [adjustSegment],
  );
  const decrement = useCallback(
    (key: DateStyleKey) => adjustSegment(key, -1),
    [adjustSegment],
  );

  return {
    value,
    dateParts,
    setSegment,
    increment,
    decrement,
    manualZero,
    setManualZero,
    props,
    tooltipState,
    setTooltipState,
  };
}

function useDateFieldTooltip({
  format,
  dateParts,
  inlineHelper,
  tooltipState,
}: {
  format: DateStyle;
  dateParts: DateValue;
  inlineHelper: 'auto' | 'always' | 'never';
  tooltipState: TooltipState;
}) {
  const triggerRef = useRef<HTMLDivElement>(null);

  const hasDayAndMonth =
    format.includes('D') && format.includes('M') && !format.includes('W');
  const shouldShowSmartInlineHelper =
    hasDayAndMonth && !tooltipState.dismissed && !!dateParts.D && !!dateParts.M;

  const shouldShowInlineHelper =
    tooltipState.focusedSegment !== null &&
    inlineHelper !== 'never' &&
    (inlineHelper === 'always' || shouldShowSmartInlineHelper);

  const {refs, floatingStyles} = useFloating({
    placement: 'bottom',
    offsetValue: 12,
    open: shouldShowInlineHelper,
  });

  useEffect(() => {
    if (triggerRef.current) {
      refs.setReference(triggerRef.current);
    }
  }, [refs]);

  return {
    shouldShowInlineHelper,
    triggerRef,
    floatingRefs: refs,
    floatingStyles,
  };
}

const SegmentTextField: React.FC<{
  dateType: DateStyleKey;
  state: DateFieldState;
}> = ({dateType, state}) => {
  const segment = useDateSegment(dateType as DateStyleKey, state);

  // labels are provided via date segment intent
  // eslint-disable-next-line jsx-a11y/control-has-associated-label
  return <TextField label="" uses={[segment]} />;
};

export const useLocalizedDateFieldFormat = (): DateStyle => {
  const calendarData = useCalendarData();

  const rawFormat: string =
    calendarData?.gregorian?.dateFormats?.short?.toLowerCase() || 'ymd';

  const inserted = new Set();
  const format = rawFormat.split('').reduce<string[]>((acc, char) => {
    if (!inserted.has(char) && (char === 'y' || char === 'm' || char === 'd')) {
      inserted.add(char);
      acc.push(char);
    }

    return acc;
  }, []);

  return format.join('').toUpperCase() as 'YMD' | 'MDY' | 'DMY';
};

/**
 * Format the week range for the tooltip content, following:
 * - Week within the same month: "Jul 8 - 14"
 * - Week spanning multiple months: "Jul 29 - Aug 4"
 */
function formatWeekRange(
  startDate: Date | undefined,
  endDate: Date | undefined,
  intl: ReturnType<typeof useIntl>,
  timeZone: string,
): string | undefined {
  if (!startDate || !endDate) {
    return undefined;
  }

  const sameMonth = startDate.getMonth() === endDate.getMonth();

  if (sameMonth) {
    const monthDay = intl.formatDateTime(startDate, {
      style: 'medium-month-day',
      timeZone,
    });
    const endDay = endDate.getDate();

    return `${monthDay} - ${endDay}`;
  }

  const startFormatted = intl.formatDateTime(startDate, {
    style: 'medium-month-day',
    timeZone,
  });
  const endFormatted = intl.formatDateTime(endDate, {
    style: 'medium-month-day',
    timeZone,
  });

  return `${startFormatted} - ${endFormatted}`;
}

/**
 * Inline helper tooltip content that shows the date format pattern
 * with the active segment highlighted.
 */
const InlineHelperContent = createView(
  ({
    format,
    focusedSegment,
    separator,
  }: {
    format: DateStyle;
    focusedSegment: DateStyleKey | null;
    separator: DateFieldProps['separator'];
  }) => {
    const formatKeys = format.split('') as DateStyleKey[];

    return (
      <view.div
        css={{
          display: 'grid',
          gap: 'space.1',
          gridTemplateColumns: `repeat(${2 * formatKeys.length - 1}, auto)`,
          alignItems: 'baseline',
          paddingY: 'xsmall',
          paddingX: 'small',
          backgroundColor: 'feedback.neutral.inverted',
          borderRadius: 'small',
          boxShadow: 'small',
        }}
      >
        {formatKeys.map((key, index) => {
          const isActive = key === focusedSegment;
          const label = inputMap[key].placeholder;
          // all odd columns are for the labels, all even are separators
          const gridColumn = 2 * index + 1;

          return (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={`${key}-${index}`}>
              <view.span
                css={[
                  css({
                    gridColumn,
                    gridRow: 1,
                    textColor: 'onFeedback.neutral.inverted',
                  }),
                  isActive
                    ? css({font: 'label.small.emphasized', opacity: 1})
                    : css({font: 'label.small', opacity: 0.5}),
                ]}
              >
                {label}
              </view.span>
              {/* hidden span to avoid unpleasant horizontal tooltip growth
               when changing the active segment's font weight */}
              <view.span
                aria-hidden="true"
                css={{
                  gridColumn,
                  gridRow: 1,
                  font: 'label.small.emphasized',
                  visibility: 'hidden',
                }}
              >
                {label}
              </view.span>
              {index < formatKeys.length - 1 ? (
                <view.span
                  css={{
                    font: 'label.small',
                    textColor: 'onFeedback.neutral.inverted',
                    opacity: 0.5,
                  }}
                >
                  {separator}
                </view.span>
              ) : null}
            </React.Fragment>
          );
        })}
      </view.div>
    );
  },
);

const WeekSegmentTextField = ({
  dateType,
  state,
  timeZone,
}: {
  dateType: DateStyleKey;
  state: DateFieldState;
  timeZone: string;
}) => {
  const intl = useIntl();
  const calendarData = useCalendarData();
  const weekNarrowPrefix = calendarData?.dateFields?.week?.narrow;
  const locale = intl.locale;

  const tooltipContent = useMemo(() => {
    const {Y: year, W: week} = state.dateParts;

    if (!year || !week) {
      return undefined;
    }

    const weekValue = `${year}-${week}`;
    const startDate = weekFieldValueToDate(
      weekValue,
      timeZone,
      locale,
      calendarData,
    );
    const endDate = weekFieldValueToEndDate(
      weekValue,
      timeZone,
      locale,
      calendarData,
    );

    return formatWeekRange(startDate, endDate, intl, timeZone);
  }, [state.dateParts, timeZone, locale, calendarData, intl]);

  const content = (
    <view.div css={{stack: 'x', gap: 'xsmall'}}>
      <WeekPrefix prefix={weekNarrowPrefix} />
      <SegmentTextField dateType={dateType} state={state} />
    </view.div>
  );

  if (!tooltipContent) {
    return content;
  }

  return (
    <Tooltip type="label" placement="top" trigger={content}>
      {tooltipContent}
    </Tooltip>
  );
};

export const DateField = DateFieldConfig.createView(
  (props) => {
    const [fieldProps, controlProps] = useFormFieldProps(props);
    const componentProps = {...fieldProps, ...controlProps};

    const {
      defaultValue,
      value,
      format,
      separator,
      onDateChange,
      onChange,
      unstable_onFieldChange,
      placeholders,
      shouldPadZeros,
      autoComplete,
      timeZone: timeZoneProp,
      inlineHelper = 'auto',
      onBlur: onBlurProp,
      ...restProps
    } = componentProps;

    const intlConfig = useIntlConfig();

    const timeZone = timeZoneProp || intlConfig.timeZone || browserTimeZone;

    const state = useDateFieldState({
      defaultValue,
      value,
      format,
      placeholders,
      onDateChange,
      onChange,
      unstable_onFieldChange,
      shouldPadZeros,
      autoComplete,
    });

    const dateFormatKeys = format.split('') as DateStyleKey[];
    const {shouldShowInlineHelper, triggerRef, floatingRefs, floatingStyles} =
      useDateFieldTooltip({
        format,
        dateParts: state.dateParts,
        inlineHelper,
        tooltipState: state.tooltipState,
      });

    return (
      <CompositeField
        {...(restProps as CompositeFieldProps)}
        invalid={props.invalid ?? (props.error ? true : undefined)}
        uses={[composite as Intent]}
        onBlur={(event) => {
          // Reset tooltip state if focus is leaving the entire field
          const {currentTarget, relatedTarget} = event;
          if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
            state.setTooltipState({focusedSegment: null, dismissed: false});
          }
          onBlurProp?.(event);
        }}
      >
        <FocusScope>
          <view.div
            css={{stack: 'x', gap: 'small', alignY: 'center'}}
            ref={triggerRef}
            data-testid="date-field-segments"
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Escape' && shouldShowInlineHelper) {
                state.setTooltipState((prev) => ({...prev, dismissed: true}));
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            {dateFormatKeys.map((type, index) => {
              const isWeekField = type === 'W';

              return (
                <React.Fragment key={type}>
                  {isWeekField ? (
                    <WeekSegmentTextField
                      dateType={type}
                      state={state}
                      timeZone={timeZone}
                    />
                  ) : (
                    <SegmentTextField dateType={type} state={state} />
                  )}

                  {index < dateFormatKeys.length - 1 ? (
                    <Separator separator={separator} />
                  ) : null}
                </React.Fragment>
              );
            })}
          </view.div>
        </FocusScope>
        {shouldShowInlineHelper ? (
          <Layer tabIndex={-1}>
            <view.div
              ref={(el) => {
                // JSDOM doesn't support `getBoundingClientRect`, ignoring positioning (see Popover.desktop.tsx for more details)
                if (
                  el instanceof HTMLDivElement &&
                  typeof jest === 'undefined'
                ) {
                  floatingRefs.setFloating(el);
                }
              }}
              css={{
                position: 'absolute',
                zIndex: 'tooltip',
              }}
              style={floatingStyles}
              role="tooltip"
            >
              <InlineHelperContent
                format={format}
                focusedSegment={state.tooltipState.focusedSegment}
                separator={separator ?? '/'}
              />
            </view.div>
          </Layer>
        ) : null}
      </CompositeField>
    );
  },
  {
    subviews: {
      inputs: {
        css: {alignX: 'start', width: 'fit'},
        uses: [
          NativeTextFieldConfig.customize({
            uses: [
              css({
                fontVariantNumeric: 'tabular-nums',
              }),
              css({
                textAlign: 'center',

                ':focus': {
                  borderRadius: 'xsmall',
                  backgroundColor: $['color.feedback.info.100'],
                },

                '::selection': {
                  backgroundColor: $['color.feedback.info.100'],
                },
              }),
            ],
            forward: {
              placeholder: true,
            },
            variants: {
              pattern: {
                [inputMap.Y.pattern]: {css: {width: '3em'}},
                [inputMap.M.pattern]: {css: {width: '2em'}},
                [inputMap.D.pattern]: {css: {width: '2em'}},
                [inputMap.W.pattern]: {css: {width: '2em'}},
              },
            },
          }),
        ],
      },
    },
  },
);
